import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import Swiper, { A11y, Navigation } from 'swiper'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)
Swiper.use([A11y, Navigation])

const SELECTORS = {
  carousel: '.js-carousels',
  horizontal: '.js-carousels--horizontal',
  stacked: '.js-carousels--stacked',
  swiper: '.js-carousels__swiper',
  wrapper: '.js-carousels__wrapper',
  counter: '.js-swiper-carousel-nav__counter__current',
  navNext: '.js-swiper_nav--next',
  navPrev: '.js-swiper_nav--prev'
}

class Carousels {
  constructor (element) {
    this.element = element
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.wrapper = this.element.querySelector(SELECTORS.wrapper)
    this.swiper = this.element.querySelector(SELECTORS.swiper)
    this.navNext = this.element.querySelector(SELECTORS.navNext)
    this.navPrev = this.element.querySelector(SELECTORS.navPrev)
    this.counter = this.element.querySelector(SELECTORS.counter)
    this.slideshow = null
    this.throttleResize = null
    this.isStart = true
    this.isEnd = false
    this.style = this.element.classList.contains('js-carousels--horizontal') ? 'horizontal' : 'stacked'
    this.init()
  }

  init() {
    this.goToFirstBound = this.goToFirst.bind(this)
    this.goToLastBound = this.goToLast.bind(this)

    this.createCarousel()
  }

  initStacked() {
    this.carousel = new Swiper(this.swiper, {
      virtualTranslate: true,
      effect: 'unlockedStacked',
      allowTouchMove: false,
      slideToClickedSlide: true,
      slidesPerView: 'auto',
      a11y: {
        enabled: true,
        firstSlideMessage: 'You are on the first image',
        lastSlideMessage: 'You are on the last image',
        nextSlideMessage: 'Advance to next image',
        prevSlideMessage: 'Go back to previous slide',
        slideLabelMessage: 'Image {{index}} of {{slidesLength}}'
      },
      navigation: {
        nextEl: this.navNext,
        prevEl: this.navPrev
      },
      on: {
        init: this.setAriaHidden,
        slideChange: this.setAriaHidden
      }
    })

    this.setRolloverEvents(true, false)

    this.carousel.on('slideChange', (swiper) => {
      this.setStackedOrder(swiper.activeIndex, swiper.slides)
    })

    this.carousel.on('activeIndexChange', (swiper) => {
      this.setRolloverEvents(swiper.isBeginning, swiper.isEnd)
    })
  }

  setAriaHidden(swiper) {
    swiper.slides.forEach(element => {
      element.ariaHidden = true
    })

    swiper.slides[swiper.activeIndex].ariaHidden = false
  }

  initHorizontal() {
    // If the entire carousel fits on-screen, skip using the carousel. The
    // visible area is the width of the swiper area plus the right content
    // margin (since the carousel runs off the right side of the page).
    const visibleWidth = this.swiper.offsetWidth + (this.element.offsetWidth - this.swiper.offsetWidth) / 2
    if (this.swiper.scrollWidth > visibleWidth) {
      this.carousel = new Swiper(this.swiper, {
        slidesPerView: 1.25,
        centeredSlides: false,
        spaceBetween: 15,
        a11y: true,
        mousewheel: {
          forceToAxis: true,
          sensitivity: 1
        },
        freeMode: {
          enabled: true,
          sticky: true,
          momentumBounce: false
        },
        breakpoints: {
          768: {
            spaceBetween: 32,
            slidesPerView: 'auto'
          }
        },
        navigation: {
          nextEl: this.navNext,
          prevEl: this.navPrev
        }
      })
    } else {
      this.element.classList.add('carousels--no-swiper')
    }
  }

  createCarousel() {
    if (this.style === 'stacked') {
      this.initStacked()
    } else {
      this.initHorizontal()
    }

    if (this.counter) {
      this.carousel.on('activeIndexChange', (swiper) => {
        this.counter.innerHTML = swiper.activeIndex + 1
      })
    }
  }

  setRolloverEvents(isFirst, isLast) {
    this.carousel.navigation.nextEl.removeEventListener('click', this.goToFirstBound)
    this.carousel.navigation.prevEl.removeEventListener('click', this.goToLastBound)

    if (isLast) {
      this.carousel.navigation.nextEl.addEventListener('click', this.goToFirstBound)
    }

    if (isFirst) {
      this.carousel.navigation.prevEl.addEventListener('click', this.goToLastBound)
    }
  }

  goToFirst() {
    this.carousel.slideTo(0)
  }

  goToLast() {
    this.carousel.slideTo(this.carousel.slides.length - 1)
  }

  setStackedOrder(current, elements) {
    const total = elements.length
    elements.forEach((element, index) => {
      element.dataset.swiperstackorder = (index + total - current) % total
    })
  }
}

export default {
  selector: SELECTORS.carousel,
  Source: Carousels
}
